/* eslint-disable gatsby/purify-html */
import React from 'react';
import DOMPurify from 'dompurify';

const shopifyRichTextToHTML = (richTextData, textOnly = false) => {
  if (!richTextData) {
    // console.log("FAIL: Rich Text Data is undefined or null.");
    return null; // Handle the case when richTextData is undefined or null
  } else {
    // console.log("PASS: Rich Text Data exists.");
  }

  let childrenArray;

  // Attempt to parse richTextData if it's a string
  if (typeof richTextData === 'string') {
    try {
      // console.log("Parsing richTextData:", richTextData);
      richTextData = JSON.parse(richTextData);
      // console.log("PASS: Successfully parsed richTextData.");
    } catch (error) {
      // console.error("FAIL: Error parsing richTextData JSON:", error);
      return null; // Handle the case when parsing fails
    }
  } else {
    // console.log("PASS: Rich Text Data is already an object.");
  }

  // Extract the children array
  childrenArray = richTextData.children;

  if (!Array.isArray(childrenArray)) {
    // console.log("FAIL: Rich Text Data is not an array.");
    return null; // Handle the case when childrenArray is not an array
  } else {
    // console.log("PASS: Rich Text Data is an array.");
  }

  let ulCount = 0;

  return childrenArray.map((node, index) => {
    switch (node.type) {
      case 'paragraph':
        // console.log(`PASS: Rendering paragraph at index ${index}.`);
        return textOnly
          ? node.children.map((child) => child.value).join("")
          : (
            <p key={index}>
              {node.children.map((child, childIndex) => child.value)}
            </p>
          );

      case 'list':
        if (node.listType === 'ordered') {
          // console.log(`PASS: Rendering ordered list at index ${index}.`);
          return textOnly
            ? node.children.map((item) => `- ${item.children.map((child) => child.value)}`).join("")
            : (
              <ol key={index}>
                {node.children.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    {item.children.map((child) => child.value)}
                  </li>
                ))}
              </ol>
            );
        } else if (node.listType === 'unordered') {
          ulCount++;
          // console.log(`PASS: Rendering unordered list at index ${index}.`);
          return textOnly
            ? node.children.map((item) => `- ${item.children.map((child) => child.value)}`).join("")
            : (
              <ul key={index} className={`ul${ulCount}`}>
                {node.children.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    {item.children.map((child) => child.value)}
                  </li>
                ))}
              </ul>
            );
        } else {
          // console.log(`FAIL: Unknown list type at index ${index}.`);
          return null;
        }

      case 'div':
        // console.log(`PASS: Rendering div at index ${index}.`);
        return textOnly
          ? node.children.map((child) => child.value).join("")
          : (
            <div key={index}>
              {node.children.map((child, childIndex) => child.value)}
            </div>
          );

      case 'span':
        // console.log(`PASS: Rendering span at index ${index}.`);
        return textOnly
          ? node.children.map((child) => child.value).join("")
          : (
            <span key={index}>
              {node.children.map((child, childIndex) => child.value)}
            </span>
          );

      case 'root':
        // console.log("PASS: Rendering root node.");
        return <ShopifyRichText key={index} richTextData={node} textOnly={textOnly} />;

      default:
        // console.log(`FAIL: Unknown node type '${node.type}' at index ${index}.`);
        return null;
    }
  });
};

const ShopifyRichText = ({ richTextData, textOnly }) => {
  try {
    if (!richTextData) {
      // console.log("FAIL: Rich Text Data is undefined or null.");
      return null; // Handle the case when richTextData is undefined or null
    } else {
      // console.log("PASS: Rich Text Data exists for rendering.");
    }

    // Check if richTextData is a valid HTML string
    if (typeof richTextData === 'string') {
      const isHTML = /<\/?[a-z][\s\S]*>/i.test(richTextData);

      if (isHTML) {
        // console.log("PASS: Rich Text Data is valid HTML, attempting to sanitize.");

        let sanitizedHTML;
        try {
          const isBrowser = typeof window !== 'undefined';
          if (isBrowser) {
            sanitizedHTML = DOMPurify.sanitize(richTextData);
            // console.log("PASS: Successfully sanitized HTML content.");
          } else {
            // console.warn("FAIL: Skipping DOMPurify due to server-side rendering.");
            sanitizedHTML = richTextData; // Cautious fallback for SSR
          }
        } catch (error) {
          // console.error("FAIL: Error during DOMPurify sanitization:", error);
          sanitizedHTML = richTextData; // Fallback to unsanitized content (not recommended)
        }

        return <div dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
      } else if (textOnly) {
        // console.log("PASS: Rendering text-only version of richTextData.");
        // This is to output ONLY the text, for instance when using it for SEO
        const renderedText = shopifyRichTextToHTML(richTextData, textOnly);
        return renderedText && renderedText.length > 0 ? renderedText[0] : null;
      } else {
        // console.log("PASS: Assuming richTextData is Shopify rich text data, rendering.");
        // If richTextData is not valid HTML, assume it's Shopify rich text data
        return <div>{shopifyRichTextToHTML(richTextData)}</div>;
      }
    } else {
      // richTextData is already parsed as an object
      // console.log("PASS: Rendering rich text data as an object.");
      return <div>{shopifyRichTextToHTML(richTextData)}</div>;
    }
  } catch (error) {
    // console.error('FAIL: Error rendering rich text:', error);
    return <div>Error rendering rich text</div>; // Provide a fallback message
  }
};

export default ShopifyRichText;